/**
 * Course settings · "Logiscool": the state of the link with Logiscool and the
 * actions that go with it.
 */
import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "../../../lib/api";
import LimoLicenceExperiment from "../LimoLicenceExperiment";
import LimoReconciliation from "../LimoReconciliation";

export default function LogiscoolSettings() {
  return (
    <div className="grid grid-cols-1 gap-4" data-testid="settings-tab-logiscool">
      <LimoStatusCard />
    </div>
  );
}

function LimoStatusCard() {
  const [state, setState] = useState(null);
  // Paid participants still waiting for a licence, against licences left.
  const [capacity, setCapacity] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/limo/status");
      setState(r.data);
      if (r.data?.ok) {
        try {
          const c = await api.get("/admin/limo/capacity");
          setCapacity(c.data);
        } catch {
          setCapacity(null);
        }
      }
    } catch (err) {
      // Ours failing and theirs failing mean different things, and conflating
      // them already cost a round of debugging at the wrong system once.
      setState({ localError: apiErrorMessage(err, "Nu am putut contacta serverul.") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const configured = state?.configured;
  const reachable = state?.ok === true;

  return (
    <section
      className="bg-white rounded-2xl border border-[#E5E7EB] p-5"
      data-testid="limo-status-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-[#373A45]">Licențiere Logiscool (LIMO)</h3>
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            state?.localError || state == null ? "bg-[#F3F4F6] text-[#6B7280]"
              : !configured ? "bg-[#FDF3E7] text-[#8a6a1a]"
              : reachable ? "bg-[#EAF4EE] text-[#2F6B4E]"
              : "bg-[#FDECEC] text-[#9b1f2c]"
          }`}
          data-testid="limo-state"
        >
          {state?.localError ? "Neverificat"
            : state == null ? "Se verifică…"
            : !configured ? "Neconfigurat"
            : reachable ? "Conectat" : "Indisponibil"}
        </span>
      </div>

      <p className="mt-2 text-sm text-[#6B7280] leading-relaxed">
        Doar citire — nicio licență nu se consumă de aici.
      </p>

      {state?.localError && (
        <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] px-3 py-2.5 text-sm text-[#6B7280]">
          Nu am putut întreba serverul nostru: <strong>{state.localError}</strong>.
          Dacă tocmai s-a făcut un deploy, reîncarcă peste un minut — nu spune
          nimic despre Logiscool.
        </div>
      )}

      {state && !state.localError && !configured && (
        <div className="mt-3 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2.5 text-sm text-[#8a6a1a]">
          Lipsesc din Render:{" "}
          <strong>{(state.diagnostics?.missing || []).join(", ")}</strong>.
        </div>
      )}

      {configured && !reachable && (
        <div className="mt-3 rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2.5 text-sm text-[#9b1f2c]">
          <div>
            Logiscool a răspuns:{" "}
            <strong>{state.version?.data?.error || "eroare necunoscută"}</strong>
          </div>
          {/* The first thing to check, and not obvious: a LIMO key is set up
              for EITHER an IP allow-list OR key+secret, never both. */}
          <p className="mt-2 text-xs leading-relaxed">
            Dacă apare eroare de autorizare, cel mai probabil cheia e
            configurată pe listă de IP-uri, nu pe cheie+secret. Cele două se
            exclud — atunci trebuie cerută o cheie de tip „key+secret", pentru
            că Render nu are IP fix.
          </p>
          {state.version?.data?.body && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer">Răspunsul lor, brut</summary>
              <pre className="mt-1 whitespace-pre-wrap break-all text-[11px] opacity-80">
                {state.version.data.body}
              </pre>
            </details>
          )}
        </div>
      )}

      {reachable && (
        <div className="mt-4 space-y-4" data-testid="limo-details">
          {/* The number that matters this week: FPED holds 30 student
              licences and has more paid enrolments than that. A warning, not
              a block — who is enrolled first is a person's decision. */}
          {capacity?.warning && (
            <div
              className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2.5 text-sm text-[#8a6a1a]"
              data-testid="limo-capacity-warning"
            >
              <strong>{capacity.pending}</strong> participanți plătiți așteaptă o
              licență de cursant, dar mai sunt doar <strong>{capacity.free}</strong>{" "}
              libere. Lipsesc <strong>{capacity.shortfall}</strong> — cere
              suplimentarea de la Logiscool.
            </div>
          )}
          {capacity && capacity.known && !capacity.warning && (
            <div className="text-xs text-[#2F6B4E]" data-testid="limo-capacity-ok">
              {capacity.pending} participanți plătiți așteaptă, {capacity.free} licențe libere — ajung.
            </div>
          )}

          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <LimoField label="Versiune API" value={state.version?.data?.version} />
            <LimoField label="Unitate" value={state.diagnostics?.unit_id} />
            <LimoField
              label="Unitatea există"
              value={state.units?.expected_present ? "da" : "NU"}
            />
            <LimoField label="Permisiuni" value={(state.abilities?.list || []).length} />
          </dl>

          {state.abilities?.list?.length > 0 && (
            <div className="text-xs text-[#6B7280]">
              <span className="font-semibold uppercase tracking-widest text-[10px]">
                Ce ne permite cheia
              </span>
              <div className="mt-1 flex flex-wrap gap-1">
                {state.abilities.list.map((a) => (
                  <span key={a} className="rounded-full bg-[#F5F6F6] border border-[#E5E7EB] px-2 py-0.5 font-mono">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* An empty pool list rendered as nothing at all is the mistake this
              card was built to avoid, and it made it in anyway: the first live
              run showed seasons, permissions and a green badge with no pools
              and no explanation, which reads as "there are none" when it could
              equally be a failed call. Both states now say so. */}
          {state.pools?.ok === false && (
            <div
              className="rounded-xl border border-[#E63946]/30 bg-[#E63946]/5 px-3 py-2.5 text-sm text-[#9b1f2c]"
              data-testid="limo-pools-error"
            >
              <div>
                Nu am putut citi pool-urile de licențe:{" "}
                <strong>{state.pools.error}</strong>
              </div>
              {state.pools.body && (
                <div className="mt-1 text-xs font-mono opacity-80 break-all">
                  {state.pools.body}
                </div>
              )}
              {/* The same endpoint asked several ways. A row that answers 200
                  is the shape our client should be using — read off their
                  reply rather than guessed at, which is how this endpoint
                  already cost two wrong attempts. */}
              {state.pools.probe?.length > 0 && (
                <details className="mt-2 text-xs" open>
                  <summary className="cursor-pointer font-semibold">
                    Ce răspunde la fiecare variantă de cerere
                  </summary>
                  <ul className="mt-1 space-y-1" data-testid="limo-pools-probe">
                    {state.pools.probe.map((r) => (
                      <li key={r.label} className="font-mono break-all">
                        <span className={r.ok ? "text-[#2F6B4E] font-bold" : "opacity-70"}>
                          {r.ok ? "✓" : "✗"} {r.label}
                        </span>
                        {" — "}
                        {r.error
                          ? r.error
                          : r.ok
                            ? `200, ${r.count ?? "?"} pool-uri (${r.shape})`
                            : `${r.status}: ${(r.body || "").slice(0, 120)}`}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          {state.pools?.ok && state.pools.items.length === 0 && (
            <div
              className="rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2.5 text-sm text-[#8a6a1a]"
              data-testid="limo-pools-empty"
            >
              {state.pools.total_visible ? (
                <>
                  Cheia vede <strong>{state.pools.total_visible}</strong> pool-uri,
                  dar niciunul nu aparține unității{" "}
                  <strong>{state.diagnostics?.unit_id}</strong>. Verifică dacă
                  unitatea e cea corectă.
                </>
              ) : (
                <>
                  Cheia nu vede niciun pool de licențe. Lista întoarce doar
                  pool-urile pentru care utilizatorul cheii e înregistrat ca
                  <strong> License Pool Manager</strong> — deci e o setare la
                  Logiscool, nu ceva ce rezolvăm din cod. Cere-le să vă
                  înregistreze cheia ca manager pe pool-urile FPED.
                </>
              )}
            </div>
          )}

          {/* The number that decides how carefully the write path has to be
              built: how many licences there actually are. */}
          {state.pools?.items?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="limo-pools">
                <thead className="text-[#6B7280] uppercase tracking-wide">
                  <tr>
                    <th className="text-left py-1.5">Pool</th>
                    <th className="text-left py-1.5">Tip</th>
                    <th className="text-right py-1.5">Alocate</th>
                    <th className="text-right py-1.5">Maxim</th>
                    <th className="text-right py-1.5">Libere</th>
                    <th className="text-left py-1.5">Notificări Logiscool</th>
                    <th className="text-left py-1.5">Materii</th>
                  </tr>
                </thead>
                <tbody>
                  {state.pools.items.map((p) => (
                    <tr key={p.id} className="border-t border-[#E5E7EB]">
                      <td className="py-1.5">
                        {p.name}
                        {/^test\b/i.test(p.name || "") && (
                          <span
                            className="ml-2 inline-flex rounded-full bg-[#F3F4F6] text-[#6B7280] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                            title="Pool de probă — nu înscrie oameni reali aici"
                          >
                            probă
                          </span>
                        )}
                      </td>
                      <td className="py-1.5">
                        <span className={
                          p.kind === "necunoscut"
                            ? "text-[#B45309] font-semibold" : "text-[#6B7280]"
                        }>
                          {p.kind}
                        </span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{p.assigned ?? "—"}</td>
                      <td className="py-1.5 text-right tabular-nums">{p.max_count ?? "—"}</td>
                      <td className={`py-1.5 text-right tabular-nums font-semibold ${
                        p.free === 0 ? "text-[#9b1f2c]" : "text-[#2F6B4E]"
                      }`}>
                        {p.free ?? "—"}
                      </td>
                      <td className="py-1.5 text-[#6B7280]">
                        {p.moderator_emails?.length
                          ? p.moderator_emails.join(", ")
                          : "niciuna"}
                      </td>
                      <td className="py-1.5 text-[#6B7280] font-mono text-[10px]">
                        {p.subject_ids?.length ? p.subject_ids.join(", ") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {state.seasons?.items?.length > 0 && (
            <div className="text-xs text-[#6B7280]">
              <span className="font-semibold uppercase tracking-widest text-[10px]">
                Sezoane
              </span>
              <div className="mt-1 flex flex-wrap gap-1">
                {state.seasons.items.map((s) => (
                  <span key={s.id} className="rounded-full bg-[#F5F6F6] border border-[#E5E7EB] px-2 py-0.5 font-mono">
                    {s.code || s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={load}
        disabled={loading}
        className="mt-4 rounded-full border border-[#E5E7EB] hover:bg-[#F5F6F6] disabled:opacity-60 text-sm font-semibold px-4 py-1.5"
        data-testid="limo-reload"
      >
        {loading ? "Se verifică…" : "Verifică din nou"}
      </button>
      {reachable && <LimoReconciliation />}
      {reachable && <LimoLicenceExperiment />}
    </section>
  );
}

function LimoField({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</dt>
      <dd className="mt-0.5 text-sm text-[#373A45]">{value ?? "—"}</dd>
    </div>
  );
}
